<script>
	// @ts-nocheck
	import { onMount } from 'svelte';
	import { Check, X, Users, Phone, Mail } from 'lucide-svelte';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import { handleAnchorClick } from '$lib/components/golf/scroll';
	import { getRezdyCalendarLink, getRezdyBookingLink } from '$lib/data/rezdyBooking';
	import { trackNavigateToCheckout, trackPhoneClick } from '$lib/utils/analytics';
	import {
		LEGENDS_PRODUCT_CODE,
		LEGENDS_PRICE,
		LEGENDS_PRICE_DISPLAY,
		legendsInclusions as inclusions
	} from '$lib/data/legends';

	const calendarLink = getRezdyCalendarLink(LEGENDS_PRODUCT_CODE);
	const bookingLink = getRezdyBookingLink(
		LEGENDS_PRODUCT_CODE,
		'legends-of-tasmania-golf-history-and-flavour-4-days-4-nights'
	);

	const notIncluded = [
		'Flights',
		'Insurance',
		'Electric golf carts (only available at Championship Golf Course, Launceston)',
		'Meals not included (other than morning tea stated above)',
		'Golf club hire (available on request)'
	];

	// Mirrors the standard tour page: the Rezdy calendar is an iframe, so the
	// only reliable signal that someone started booking is focus moving into it.
	onMount(() => {
		let hasTracked = false;

		const trackIframeInteraction = () => {
			const iframe = document.querySelector('iframe.rezdy');
			if (document.activeElement === iframe && !hasTracked) {
				hasTracked = true;
				trackNavigateToCheckout({
					productCode: LEGENDS_PRODUCT_CODE,
					tourName: 'Legends of Tasmania: Golf, History & Flavour',
					price: LEGENDS_PRICE,
					productType: 'MULTIDAYTOUR'
				});
			}
		};

		window.addEventListener('blur', trackIframeInteraction);
		return () => window.removeEventListener('blur', trackIframeInteraction);
	});
</script>

<section id="book" class="bg-white pb-16 pt-16 lg:pb-24 lg:pt-24">
	<div class="mx-auto max-w-7xl px-4">
		<div class="grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-5">
			<!-- Inclusions -->
			<div class="lg:col-span-3">
				<h2 class="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
					What's included
				</h2>

				<ul class="mt-8 flex flex-col divide-y border-y">
					{#each inclusions as inclusion}
						<li class="flex items-start gap-4 py-4">
							<div
								class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tt-red"
							>
								<Check class="h-4 w-4 text-background" />
							</div>
							<div>
								<p class="font-heading font-semibold leading-7">{inclusion.title}</p>
								{#if inclusion.detail}
									<p class="text-sm leading-6 text-gray-600">{inclusion.detail}</p>
								{/if}
							</div>
						</li>
					{/each}
				</ul>

				<h3 class="mt-12 font-heading text-2xl font-semibold tracking-tight">Not included</h3>
				<ul class="mt-6 flex flex-col divide-y border-y">
					{#each notIncluded as item}
						<li class="flex items-start gap-4 py-4">
							<div
								class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200"
							>
								<X class="h-4 w-4 text-gray-600" />
							</div>
							<p class="leading-7 text-gray-700">{item}</p>
						</li>
					{/each}
				</ul>

				<div class="mt-8 flex items-center gap-3 rounded-2xl bg-secondary p-5">
					<Users class="h-5 w-5 shrink-0 text-[#194139]" />
					<p class="text-sm leading-6 text-gray-700">
						Small group touring, <strong class="font-semibold text-foreground"
							>maximum 24 travellers</strong
						>.
					</p>
				</div>
			</div>

			<!-- Booking -->
			<div class="lg:col-span-2">
				<div class="lg:sticky lg:top-24">
					<Card.Root class="overflow-hidden rounded-2xl border-none bg-[#f4f5f7] shadow-none">
						<Card.Header class="pb-4">
							<div class="flex items-baseline gap-2">
								<span class="text-sm font-medium text-muted-foreground">From AUD</span>
								<span class="font-heading text-3xl font-bold">${LEGENDS_PRICE_DISPLAY}</span>
								<span class="text-sm font-medium text-muted-foreground">per person</span>
							</div>
							<Card.Title class="pt-2 font-heading text-xl">Check dates & book</Card.Title>
							<Card.Description>
								Select a departure below to secure your place on the tour, or
								<a
									href="#enquiry"
									onclick={handleAnchorClick}
									data-sveltekit-noscroll
									class="font-medium underline underline-offset-4">contact us</a
								> to book this as a private tour.
							</Card.Description>
						</Card.Header>
						<Card.Content class="mx-0 mt-0 px-0 pt-0">
							{#if calendarLink}
								<iframe
									seamless=""
									width="100%"
									height="500px"
									frameborder="0"
									class="rezdy"
									title="Legends of Tasmania booking calendar"
									src={calendarLink}
								></iframe>
							{:else}
								<div class="px-6 pb-6">
									<Button
										href="#enquiry"
										onclick={handleAnchorClick}
										data-sveltekit-noscroll
										class="w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
										>Enquire about dates</Button
									>
								</div>
							{/if}
						</Card.Content>
					</Card.Root>

					<div class="mt-6 rounded-2xl border p-6">
						<p class="font-heading font-semibold">Booking for a group?</p>
						<p class="mt-2 text-sm leading-6 text-gray-600">
							Talk to us about taking the whole tour privately, or tailoring it for your club or
							company.
						</p>
						<Button
							href="#enquiry"
							onclick={handleAnchorClick}
							data-sveltekit-noscroll
							variant="outline"
							class="mt-4 w-full rounded-full border-2 font-semibold">Send an enquiry</Button
						>
						<div class="mt-4 flex flex-col gap-2 text-sm">
							<a
								href="tel:0409404337"
								onclick={trackPhoneClick}
								class="flex items-center gap-2 text-gray-600 hover:text-tt-red"
							>
								<Phone class="h-4 w-4" /> 0409 404 337
							</a>
							<a
								href="mailto:golf@tassietours.com"
								class="flex items-center gap-2 text-gray-600 hover:text-tt-red"
							>
								<Mail class="h-4 w-4" /> golf@tassietours.com
							</a>
						</div>
					</div>

					{#if bookingLink}
						<noscript>
							<Button
								href={bookingLink}
								target="_blank"
								rel="noopener noreferrer"
								class="mt-6 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
								>Book on Rezdy</Button
							>
						</noscript>
					{/if}
				</div>
			</div>
		</div>
	</div>
</section>
